import { Over } from '@dnd-kit/core';
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface TopRowOverContextProps {
  isShowTop: boolean;
  getHoverPosition: (
    over: Over,
    trRef: React.RefObject<HTMLTableRowElement>
  ) => void;
}

const TopRowOverContext = createContext<TopRowOverContextProps | undefined>(
  undefined
);

export const useTopRowOverContext = () => {
  const context = useContext(TopRowOverContext);
  if (!context) {
    throw new Error(
      'useTopRowOverContext must be used within a TopRowOverProvider'
    );
  }
  return context;
};

export const TopRowOverProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isShowTop, setIsShowTop] = useState<boolean>(false);

  const getHoverPosition = useCallback(
    (over: Over, trRef: React.RefObject<HTMLTableRowElement>) => {
      if (!over || !trRef.current) return false;
      const { top, height } = trRef.current.getBoundingClientRect();
      const trMiddle = top + height / 2;
      const overMiddle = over.rect.top + over.rect.height / 2;
      setIsShowTop(trMiddle < overMiddle);
    },
    []
  );

  const value = useMemo(
    () => ({
      isShowTop,
      getHoverPosition,
    }),
    [isShowTop, getHoverPosition]
  );

  return (
    <TopRowOverContext.Provider value={value}>
      {children}
    </TopRowOverContext.Provider>
  );
};
